import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { domain } from "../consts";
import { InviteNotFound, SVGS, TestimonialCard } from "../components";
import { AlertContext } from "../context/AlertContext.jsx";
import { ModalContext } from "../context/ModalContext.jsx";
import { AuthContext } from "../context/AuthContext.jsx";
import {
  DashboardInviteCardStrings,
  DashboardStrings,
  TestimonialCardStrings,
} from "../consts/strings.js";
import { HSOverlay } from "preline/preline.js";
import Tooltip from "../components/Tooltip.jsx";

const Dashboard = () => {
  const { handleAlert } = useContext(AlertContext);
  const [rsvpArr, setRsvpArr] = useState([]);
  const [activeRsvp, setActiveRsvp] = useState([]);
  const [inactiveRsvp, setInactiveRsvp] = useState([]);
  const [feedback, setFeedback] = useState(null);

  const countColor = (tier, count) => {
    const limits = {
      0: 20,
      1: 80,
    };

    const limit = limits[tier];

    if (tier === 2) return "text-teal-500";

    const percentage = (count / limit) * 100;

    if (percentage >= 90) {
      return "text-red-500";
    } else if (percentage >= 80) {
      return "text-orange-500";
    }

    return "text-teal-500";
  };

  useEffect(() => {
    if (window.location.search === "?newInvite") {
      handleAlert(DashboardStrings.inviteCreatedAlert, "info");
    }
  }, []);

  useEffect(() => {
    getRsvps();
    getUserFeedback();
  }, []);

  useEffect(() => {
    setActiveRsvp(rsvpArr?.filter((el) => el.paymentVerified));
    setInactiveRsvp(rsvpArr?.filter((el) => !el.paymentVerified));
  }, [rsvpArr]);

  const getUserFeedback = async () => {
    try {
      const response = await fetch(`${domain}/feedback`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setFeedback(data);
    } catch (error) {
      console.error("Error fetching RSVPs:", error);
    }
  };

  const getRsvps = async () => {
    try {
      const response = await fetch(`${domain}/rsvp`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setRsvpArr(data);
    } catch (error) {
      console.error("Error fetching RSVPs:", error);
    }
  };

  const handleActivate = (e) => {
    const body = {
      rsvp_id: e.target.value,
    };
    try {
      fetch(`${domain}/create-checkout-session`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
    } catch (error) {
      console.error("Error fetching RSVPs:", error);
    }
  };

  return (
    <>
      <div className="max-w-[85rem] mb-20 px-4 py-4 sm:px-6 lg:px-8 lg:py-4 mx-auto grid grid-cols-2 gap-4 gap-y-14">
        <div className="mb-4 py-4 col-span-2 md:col-span-1 w-full min-h-[20rem] max-h-[20rem]">
          <p className="mb-4 text-gray-600   text-xl font-bold">
            {DashboardStrings.activeInvitesHeader}
          </p>
          <div className="w-full border rounded-2xl shadow-xl overflow-x-auto min-h-[20rem] max-h-[20rem]">
            <div className="w-full flex flex-row flex-wrap ">
              {activeRsvp?.length > 0 ? (
                activeRsvp?.map((rsvp) => (
                  <RSVPCard
                    key={rsvp.id}
                    rsvp={rsvp}
                    handleActivate={handleActivate}
                    getRsvps={getRsvps}
                    countColor={countColor}
                  />
                ))
              ) : (
                <div className="p-4 w-full flex justify-center">
                  <InviteNotFound active={true} />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mb-4 py-4 col-span-2 md:col-span-1 w-full min-h-[20rem] max-h-[20rem]">
          <p className="mb-4 text-gray-600   text-xl font-bold">
            {DashboardStrings.inactiveInvitesHeader}
          </p>
          <div className="w-full border rounded-2xl shadow-xl overflow-x-auto min-h-[20rem] max-h-[20rem]">
            <div className="w-full flex flex-row flex-wrap">
              {inactiveRsvp?.length > 0 ? (
                inactiveRsvp.map((rsvp) => (
                  <RSVPCard
                    key={rsvp.id}
                    rsvp={rsvp}
                    handleActivate={handleActivate}
                    getRsvps={getRsvps}
                    countColor={countColor}
                  />
                ))
              ) : (
                <div className="p-4 w-full flex justify-center">
                  <InviteNotFound />
                </div>
              )}
            </div>
          </div>
        </div>
        <LeaveFeedback
          feedback={feedback}
          getUserFeedback={getUserFeedback}
          setFeedback={setFeedback}
        />
      </div>
    </>
  );
};

const RSVPCard = ({ rsvp, getRsvps, countColor }) => {
  const getTierMaxCount = (tier) => {
    switch (tier) {
      case 0:
        return "20";
      case 1:
        return "80";
      case 2:
        return "\u221E";
      default:
        "";
    }
  };
  const getAvailableUpgrades = (currentTier) => {
    switch (currentTier) {
      case 0:
        return [
          {
            label: "Nadogradi u Prošireni paket - do 80 uzvanika (29€)",
            value: 1,
          },
          {
            label:
              "Nadogradi u Premium paket - neograničenen broj uzvanika (69€)",
            value: 2,
          },
        ];
      case 1:
        return [
          {
            label:
              "Nadogradi u Premium paket - neograničenen broj uzvanika (40€)",
            value: 2,
          },
        ];
      default:
        return [];
    }
  };
  const [loading, setIsLoading] = useState(false);
  const { handleAlert } = useContext(AlertContext);
  const { showModal } = useContext(ModalContext);

  const handleDeleteClick = () => {
    HSOverlay.open("#hs-slide-up-animation-modal");

    showModal(
      {
        eventName: rsvp.eventName,
        text: DashboardInviteCardStrings.deleteAlert,
      },
      "delete",
      handleDelete,
    );
  };
  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${domain}/rsvp/${rsvp.id}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        getRsvps();
      }
      // handleOverlay();
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching RSVPs:", error);
      // handleOverlay();
      setIsLoading(false);
    }
  };
  const handleShare = async () => {
    const shareData = {
      title: rsvp.eventName,
      text: `Pozivam te na moj dogadaj: ${rsvp.eventName} ${new Date(
        rsvp.date,
      ).toLocaleDateString("hr-HR")}`,
      url: `https://pozivnica.hr/${rsvp.slug}`,
    };
    if (navigator?.share) {
      try {
        navigator.share(shareData);
      } catch (err) {
        console.error("Error sharing:", err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareData.url);
        handleAlert(DashboardInviteCardStrings.shareAlert, "info");
      } catch (err) {
        console.error("Error copying to clipboard:", err);
      }
    }
  };

  return (
    <>
      <div
        className={`flex flex-col w-full pb-4 m-4 bg-gray-50 border border-t-4 ${
          !rsvp.paymentVerified ? "border-t-gray-400" : "border-t-teal-600"
        } shadow-xl rounded-xl        /[.7]`}
      >
        <div className="p-4 md:p-5 flex flex-row justify-between flex-wrap">
          <div className="flex flex-col w-full">
            <div className="flex flex-row justify-between items-center w-full">
              <Link
                to={`/${rsvp.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className=""
              >
                <p className="mt-3 text-teal-500   text-md sm:text-lg font-bold flex flex-row">
                  {rsvp.eventName} <SVGS.TopRightArrowSVG />
                </p>
              </Link>

              {rsvp.paymentVerified && (
                <div className="flex flex-row items-center gap-x-4">
                  {rsvp.tier < 2 && (
                    <div className="flex flex-col items-center">
                      <div className="hs-dropdown relative inline-flex">
                        <button
                          type="button"
                          className="p-1 mt-3 rounded-md text-xs sm:text-sm font-bold flex items-center justify-center border-2 border-teal-500 text-gray-600 hs-dropdown-toggle hover:scale-110 transition-transform duration-200 ease-in-out"
                        >
                          Nadogradi
                        </button>
                        <div className="hs-dropdown-menu transition-[opacity,margin] duration hs-dropdown-open:opacity-100 opacity-0 hidden min-w-48 bg-white shadow-md rounded-lg p-2 mt-2         z-10">
                          <div className="py-2 first:pt-0 last:pb-0">
                            {getAvailableUpgrades(rsvp.tier).map((upgrade) => (
                              <form
                                key={upgrade.value}
                                action={`${domain}/upgrade-tier`}
                                method="POST"
                                className="w-full"
                              >
                                <input
                                  type="hidden"
                                  name="rsvp_id"
                                  value={rsvp.id}
                                />
                                <input
                                  type="hidden"
                                  name="new_tier"
                                  value={upgrade.value}
                                />
                                <button
                                  type="submit"
                                  className="w-full flex items-center gap-x-3.5 py-2 px-3 rounded-lg text-sm text-gray-800 hover:bg-gray-100 focus:outline-none focus:bg-gray-100    :bg-gray-700  :text-gray-300  :bg-gray-700"
                                >
                                  {upgrade.label}
                                </button>
                              </form>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <Tooltip content="Broj uzvanika">
                    <p
                      className={`mt-3 ${countColor(
                        rsvp.tier,
                        rsvp.guestCount,
                      )}   text-xs sm:text-sm font-bold flex flex-row gap-x-1`}
                    >
                      <span>
                        <SVGS.PeopleSVG />
                      </span>
                      {rsvp.guestCount} / {getTierMaxCount(rsvp.tier)}
                    </p>
                  </Tooltip>
                </div>
              )}
            </div>
            <p className="mt-1 text-gray-500   text-xs">
              {new Date(rsvp.date).toLocaleDateString("hr-HR")}
            </p>
            <p className="mt-1 text-teal-600   text-xs">
              pozivnica.hr/<span>{rsvp.slug}</span>
            </p>
          </div>
        </div>
        <div className="p-4 w-full flex">
          <div className="flex w-full flex-row justify-start gap-x-5">
            {!rsvp.paymentVerified && (
              <div className="animate-bounce">
                <form
                  action={`${domain}/create-checkout-session`}
                  method="POST"
                >
                  <input type="hidden" name="rsvp_id" value={rsvp.id} />
                  <button type="submit" className="flex flex-col items-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-500 text-white">
                      <SVGS.ActivateSVG />
                    </div>
                    <span className="text-xs mt-2 font-semibold">
                      {DashboardInviteCardStrings.activate}
                    </span>
                  </button>
                </form>
              </div>
            )}
            {rsvp.paymentVerified && (
              <Link
                to={`/uzvanici/${rsvp.id}`}
                className="flex flex-col items-center"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-500 text-white">
                  <SVGS.PeopleSVG />
                </div>
                <span className="text-xs mt-2 font-semibold">
                  {DashboardInviteCardStrings.guests}
                </span>
              </Link>
            )}
            <Link
              to={`/uredi-pozivnicu/${rsvp.id}`}
              className="flex flex-col items-center"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full border border-teal-500 text-teal-500">
                <SVGS.EditSVG />
              </div>
              <span className="text-xs mt-2 font-semibold">
                {DashboardInviteCardStrings.edit}
              </span>
            </Link>
            {rsvp.paymentVerified && (
              <>
                <button
                  onClick={handleShare}
                  className="flex flex-col items-center"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full border border-teal-500 text-teal-500">
                    <SVGS.ShareSVG />
                  </div>
                  <span className="text-xs mt-2 font-semibold">
                    {DashboardInviteCardStrings.share}
                  </span>
                </button>
              </>
            )}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full border border-red-600 text-teal-600">
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className="hs-dropup-toggle w-full h-full flex flex-nowrap justify-between items-center text-sm font-semibold rounded-lg border border-transparent text-red-600 hover:text-red-800 disabled:opacity-50 disabled:pointer-events-none    :text-red-400  :outline-none  :ring-1  :ring-gray-600"
                  data-hs-overlay="#hs-slide-up-animation-modal"
                >
                  <div className="w-full h-full flex justify-center items-center">
                    <SVGS.DeleteSVG />
                  </div>
                </button>
              </div>
              <span className="text-xs mt-2 font-semibold">
                {DashboardInviteCardStrings.delete}
              </span>
            </div>
          </div>
        </div>
        {loading && (
          <div className="flex justify-center mt-4">
            <span
              className="animate-spin inline-block w-8 h-8 border-4 border-current border-t-transparent text-white rounded-full"
              role="status"
              aria-label="loading"
            ></span>
          </div>
        )}
      </div>
    </>
  );
};

const LeaveFeedback = ({ feedback, getUserFeedback, setFeedback }) => {
  const { auth } = useContext(AuthContext);
  return (
    <div className="mb-4 py-4 col-span-2 md:col-span-1 w-full min-h-[20rem] max-h-[20rem]">
      <p className="mb-4 text-gray-600   text-xl font-bold">
        {DashboardStrings.testimonialHeader}
      </p>
      <div className="w-full p-2 border rounded-2xl shadow-xl overflow-x-auto min-h-fit max-h-fit">
        <section className={`${!feedback?.id && "hidden"}`}>
          <FeedbackCard
            feedback={feedback}
            user={auth.user}
            getUserFeedback={getUserFeedback}
            setFeedback={setFeedback}
          />
        </section>
        <section className={`${feedback?.id && "hidden"}`}>
          <CreateFeedback getUserFeedback={getUserFeedback} />
        </section>
      </div>
    </div>
  );
};

const FeedbackCard = ({ feedback, user, getUserFeedback, setFeedback }) => {
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(feedback?.feedbackText);
  const [score, setScore] = useState(feedback?.score || 0);
  const [hoveredScore, setHoveredScore] = useState(0);
  const { showModal } = useContext(ModalContext);
  const { handleAlert } = useContext(AlertContext);

  useEffect(() => {
    if (feedback?.score) setScore(feedback.score);
  }, [feedback]);

  const [formData, setFormData] = useState({
    score: feedback?.score,
    feedbackText: feedback?.feedbackText,
  });
  const handleDeleteClick = () => {
    showModal(
      {
        eventName: "Recenzija",
        text: TestimonialCardStrings.deleteAlert,
      },
      "delete",
      handleDeleteFeedback,
    );
  };

  const handleDeleteFeedback = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${domain}/feedback/${feedback.id}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        handleAlert(TestimonialCardStrings.deleteSuccess, "info");
        setFeedback(null);
      } else {
        handleAlert(TestimonialCardStrings.deleteError, "error");
      }
    } catch (error) {
      console.error("Error deleting feedback:", error);
      handleAlert(TestimonialCardStrings.deleteError, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleMouseEnter = (index) => {
    setHoveredScore(index);
  };

  const handleMouseLeave = () => {
    setHoveredScore(0);
  };

  const handleClick = (index) => {
    setScore(index);
    setFormData({ ...formData, score: index });
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleTextChange = (e) => {
    setEditedText(e.target.value);
    setFormData({ ...formData, feedbackText: e.target.value });
  };

  const handleCancelClick = (e) => {
    e.preventDefault();

    setFormData({
      score: feedback.score,
      feedbackText: feedback.feedbackText,
    });

    setScore(feedback.score);
    setEditedText(feedback.feedbackText);

    setIsEditing(false);
  };

  const handleSaveClick = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsEditing(false);
    try {
      const response = await fetch(`${domain}/feedback/${feedback.id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        getUserFeedback();
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching RSVPs:", error);
      setLoading(false);
    }
  };

  return (
    <>
      <div className="aspect-auto p-8 border border-gray-100 rounded-3xl bg-white     shadow-2xl shadow-gray-600/10  ">
        <div className="flex gap-4">
          <div className="w-12 h-12">
            {user?.avatar ? (
              <img src={user?.avatar} className="h-12 w-12 rounded-full" />
            ) : (
              <SVGS.DefaultAvatarSVG />
            )}
          </div>
          <div>
            <h6 className="text-lg font-medium text-gray-700  ">
              {user?.name} {user?.surname}
            </h6>
            <p className="text-sm text-gray-500  ">
              {new Date(feedback?.createdAt).toLocaleDateString("hr-HR")}
            </p>
          </div>
        </div>
        {isEditing ? (
          <>
            <div className="w-full flex flex-row flex-wrap py-4">
              {Array.from({ length: 5 }, (_, index) => (
                <div
                  key={index}
                  className={`cursor-pointer text-gray-400   p-1 ${
                    hoveredScore >= index + 1 || score >= index + 1
                      ? "text-yellow-500"
                      : ""
                  }`}
                  onMouseEnter={() => handleMouseEnter(index + 1)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => handleClick(index + 1)}
                >
                  <SVGS.StarRating />
                </div>
              ))}
            </div>
            <textarea
              className="mt-4 py-3 px-4 block w-full border-gray-200 rounded-lg text-lg focus:border-teal-500 focus:ring-teal-500          :ring-neutral-600"
              rows="3"
              value={editedText}
              onChange={handleTextChange}
            ></textarea>
          </>
        ) : (
          <>
            <div className="w-full flex flex-row flex-wrap py-4">
              {Array.from({ length: 5 }, (_, index) => (
                <div
                  key={index}
                  className={`text-gray-400   p-1 ${
                    score >= index + 1 ? "text-yellow-500" : ""
                  }`}
                >
                  <SVGS.StarRating />
                </div>
              ))}
            </div>
            <p className="mt-2 text-lg">{feedback?.feedbackText}</p>
          </>
        )}
      </div>
      <div className="w-full flex justify-end flex-wrap p-4 gap-x-4">
        {loading ? (
          <button
            type="submit"
            className="py-3 px-4 min-w-36 flex justify-center gap-x-4 text-sm font-semibold rounded-lg border border-transparent bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:pointer-events-none  :outline-none  :ring-1  :ring-gray-600"
          >
            {TestimonialCard.sendTestimonial}
            <span
              className="animate-spin inline-block size-4 border-[3px] border-current border-t-transparent text-white rounded-full"
              role="status"
              aria-label="loading"
            ></span>
          </button>
        ) : isEditing ? (
          <>
            <button
              type="button"
              className="mr-2 py-3 px-4 min-w-36 flex justify-center gap-x-4 text-sm font-semibold rounded-lg border border-transparent bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:pointer-events-none  :outline-none  :ring-1  :ring-gray-600"
              onClick={handleCancelClick}
            >
              {TestimonialCardStrings.cancelTestimonial}
            </button>
            <button
              type="submit"
              className="py-3 px-4 min-w-36 flex justify-center gap-x-4 text-sm font-semibold rounded-lg border border-transparent bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:pointer-events-none  :outline-none  :ring-1  :ring-gray-600"
              onClick={handleSaveClick}
            >
              {TestimonialCardStrings.saveTestimonial}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="py-3 px-4 min-w-36 flex justify-center gap-x-4 text-sm font-semibold rounded-lg border border-transparent bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:pointer-events-none  :outline-none  :ring-1  :ring-gray-600"
            onClick={handleEditClick}
          >
            {TestimonialCardStrings.editTestimonial}
          </button>
        )}
        <button
          type="button"
          className={`hs-dropup-toggle py-3 px-4 min-w-36 flex justify-center gap-x-4 text-sm font-semibold rounded-lg border border-transparent bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:pointer-events-none  :outline-none  :ring-1  :ring-gray-600 ${
            isEditing && "hidden"
          }`}
          onClick={handleDeleteClick}
          data-hs-overlay="#hs-slide-up-animation-modal"
        >
          {TestimonialCardStrings.deleteTestimonial}
        </button>
      </div>
    </>
  );
};

const CreateFeedback = ({ getUserFeedback }) => {
  const { handleAlert } = useContext(AlertContext);
  const [hoveredScore, setHoveredScore] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    score: 0,
    feedbackText: "",
  });

  const handleMouseEnter = (index) => {
    setHoveredScore(index);
  };

  const handleMouseLeave = () => {
    setHoveredScore(0);
  };

  const handleClick = (index) => {
    setFormData({ ...formData, score: index });
  };
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (formData.score === 0) {
      handleAlert(TestimonialCardStrings.emptyAlert, "warning");
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${domain}/feedback`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        getUserFeedback();
        setFormData({
          score: 0,
          feedbackText: "",
        });
      }
      // handleOverlay();
      setLoading(false);
    } catch (error) {
      console.error("Error fetching RSVPs:", error);
      // handleOverlay();
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="w-full flex flex-row flex-wrap p-4">
          {Array.from({ length: 5 }, (_, index) => (
            <div
              key={index}
              onMouseEnter={() => handleMouseEnter(index + 1)}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleClick(index + 1)}
              className={`cursor-pointer text-gray-400   p-1 ${
                hoveredScore >= index + 1 || formData.score >= index + 1
                  ? "text-yellow-500"
                  : ""
              }`}
            >
              <SVGS.StarRating />
            </div>
          ))}
        </div>
        <div className="w-full flex flex-row flex-wrap p-4">
          <textarea
            className="py-3 px-4 block w-full border-gray-200 rounded-lg text-lg focus:border-teal-500 focus:ring-teal-500 disabled:opacity-50 disabled:pointer-events-none          :ring-neutral-600"
            name="feedbackText"
            value={formData.feedbackText}
            onChange={handleChange}
            rows="3"
            placeholder={TestimonialCardStrings.writeTestimonial}
          ></textarea>
        </div>
        <div className="w-full flex justify-end flex-wrap p-4">
          {loading ? (
            <button
              type="submit"
              className="py-3 px-4 min-w-36 flex justify-center gap-x-4 text-sm font-semibold rounded-lg border border-transparent bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:pointer-events-none  :outline-none  :ring-1  :ring-gray-600"
            >
              {TestimonialCardStrings.sendTestimonial}
              <span
                className="animate-spin inline-block size-4 border-[3px] border-current border-t-transparent text-white rounded-full"
                role="status"
                aria-label="loading"
              ></span>
            </button>
          ) : (
            <button
              type="submit"
              className="py-3 px-4 min-w-36 flex justify-center gap-x-4 text-sm font-semibold rounded-lg border border-transparent bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:pointer-events-none  :outline-none  :ring-1  :ring-gray-600"
            >
              {TestimonialCardStrings.saveTestimonial}
            </button>
          )}
        </div>
      </form>
    </>
  );
};

export default Dashboard;
